import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import RentalContractFlow from './RentalContractFlow';
import { daysBetween, describeRateLine } from './lib/rentalDays';
// No pricing math lives in this file: every number shown comes from
// POST /api/public/rentals/price (server lib/totals.js), and order create
// re-runs the same function and refuses a stale expected_total.

const API = process.env.REACT_APP_API_URL || 'https://ivision-crm-backend-production.up.railway.app';
const fmt = (n) => n ? `$${Number(n).toFixed(0)}` : '$0';
const fmtFull = (n) => `$${Number(n||0).toFixed(2)}`;

export default function PublicRentalStorefront() {
  const [items, setItems]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [cart, setCart]             = useState([]);
  const [step, setStep]             = useState('browse');
  const [form, setForm]             = useState({ from_date:'', to_date:'', fulfillment_type:'pickup', notes:'', client_name:'', client_email:'', client_phone:'', client_company:'', delivery_address:'', delivery_miles:0 });
  const [distLoading, setDistLoading] = useState(false);
  const [distError, setDistError]     = useState('');
  const [cartConflicts, setCartConflicts] = useState({}); // itemId -> bool
  const [quoteForm, setQuoteForm]   = useState({ name:'', email:'', phone:'', message:'', from_date:'', to_date:'', fulfillment_type:'pickup', delivery_address:'' });
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [quoteSuccess, setQuoteSuccess]       = useState(false);
  const nameRef  = useRef();
  const emailRef = useRef();
  const phoneRef = useRef();
  const [submitting, setSubmitting] = useState(false);
  const [catFilter, setCatFilter]   = useState('all');
  const [search, setSearch]         = useState('');
  const [sortBy, setSortBy]         = useState('default');
  const [dateFrom, setDateFrom]     = useState('');
  const [dateTo, setDateTo]         = useState('');
  const [availability, setAvailability] = useState({});
  const [availLoading, setAvailLoading] = useState(false);
  const [showCart, setShowCart]     = useState(false);
  const [contractOrder, setContractOrder] = useState(null);
  const [successOrderId, setSuccessOrderId] = useState(null);
  const debounceRef = useRef(null);

  // Portal sign-in state
  const [portalStep, setPortalStep]     = useState('idle'); // idle | sending | code | done
  const [portalEmail, setPortalEmail]   = useState('');
  const [portalCode, setPortalCode]     = useState('');
  const [portalClient, setPortalClient] = useState(null);  // { name, email, discount_percent, ... }
  const [portalToken, setPortalToken]   = useState(null);
  const [portalError, setPortalError]   = useState('');
  const SUBDOMAIN = 'onthegrind';

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      try { localStorage.removeItem('ivision_pending_booking'); } catch {}
      setSuccessOrderId(params.get('order'));
      setStep('success');
      window.history.replaceState({}, '', '/rent');
    }
  }, []);

  const [settings, setSettings] = useState({ free_miles: 10, rate_per_mile: 2.50, min_delivery_fee: 0, hold_minutes: 30 });
  // Business identity (name, city, state, phone) — from the tenant's
  // default location via /settings. Nothing about the business is
  // hardcoded in this page.
  const [business, setBusiness] = useState({ name: 'Equipment Rentals', city: '', state: '', phone: '' });
  const bizPlace = [business.city, business.state].filter(Boolean).join(', ');

  useEffect(() => {
    axios.get(`${API}/api/public/rentals/items`)
      .then(r => setItems(r.data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    axios.get(`${API}/api/public/rentals/settings`)
      .then(r => { if (r.data?.settings) setSettings(p => ({ ...p, ...r.data.settings })); if (r.data?.business) setBusiness(p => ({ ...p, ...r.data.business })); })
      .catch(() => {});
  }, []);

  // ── Booking in progress ("pending"): an unpaid order this browser
  //    created, remembered by its public token until the hold expires, so
  //    closing the modal never strands the customer (or the unit).
  const PENDING_KEY = 'ivision_pending_booking';
  const readPending = () => { try { return JSON.parse(localStorage.getItem(PENDING_KEY) || 'null'); } catch { return null; } };
  const writePending = (v) => { try { v ? localStorage.setItem(PENDING_KEY, JSON.stringify(v)) : localStorage.removeItem(PENDING_KEY); } catch {} };
  const [pending, setPending] = useState(null);       // { token, order_number, hold_expires_at }
  const [closePrompt, setClosePrompt] = useState(false);
  const [pendingBusy, setPendingBusy] = useState(false);
  const holdUntilText = (iso) => iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';
  const holdNotice = (iso) => `Your equipment is held for ${settings.hold_minutes || 30} minutes while you sign and pay${iso ? ` — until ${holdUntilText(iso)}` : ''}.`;

  // Shape a server order for the contract flow (same shape submitRequest builds).
  const toContractOrder = (o) => ({
    ...o,
    items: (o.rental_order_items || []).map(li => ({ item_name: li.item_name, qty: li.quantity, rate_type: li.rate_type, rate_amount: li.unit_price, unit_price: li.unit_price, subtotal: li.total_price })),
    _prefill: { signer_name: '', signer_email: o.client_email || '', signer_phone: o.client_phone || '' },
  });

  useEffect(() => {
    const stored = readPending();
    if (!stored?.token) return;
    if (!stored.hold_expires_at || new Date(stored.hold_expires_at) <= new Date()) { writePending(null); return; }
    axios.get(`${API}/api/public/rentals/o/${stored.token}`)
      .then(r => {
        const o = r.data?.order;
        if (o && o.status === 'draft' && o.hold_expires_at && new Date(o.hold_expires_at) > new Date()) {
          const p = { token: stored.token, order_number: o.order_number, hold_expires_at: o.hold_expires_at };
          writePending(p); setPending(p);
        } else { writePending(null); }
      })
      .catch(() => { writePending(null); });
  }, []);

  // The hold lapsing while the banner is up: drop it.
  useEffect(() => {
    if (!pending?.hold_expires_at) return;
    const ms = new Date(pending.hold_expires_at) - new Date();
    if (ms <= 0) { setPending(null); writePending(null); return; }
    const t = setTimeout(() => { setPending(null); writePending(null); }, ms);
    return () => clearTimeout(t);
  }, [pending]);

  const cancelPending = async (token = pending?.token) => {
    if (!token) return;
    setPendingBusy(true);
    try { await axios.post(`${API}/api/public/rentals/o/${token}/cancel`); }
    catch (e) { alert(e.response?.data?.error || 'Could not cancel this booking — please contact us.'); }
    writePending(null); setPending(null); setContractOrder(null); setClosePrompt(false); setStep('cart');
    setPendingBusy(false);
  };
  const resumePending = async () => {
    if (!pending?.token) return;
    setPendingBusy(true);
    try {
      const r = await axios.get(`${API}/api/public/rentals/o/${pending.token}`);
      const o = r.data?.order;
      if (!o || o.status !== 'draft') { writePending(null); setPending(null); alert('That booking is no longer available. Please start a new one.'); }
      else { setContractOrder(toContractOrder(o)); setStep('contract'); }
    } catch { writePending(null); setPending(null); }
    setPendingBusy(false);
  };

  // Fetch availability when dates change
  useEffect(() => {
    if (!dateFrom || !dateTo || dateTo <= dateFrom) { setAvailability({}); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setAvailLoading(true);
      try {
        const r = await axios.get(`${API}/api/public/rentals/availability?from=${dateFrom}&to=${dateTo}`);
        if (r.data.success) setAvailability(r.data.availability || {});
      } catch {}
      setAvailLoading(false);
    }, 500);
  }, [dateFrom, dateTo]);

  const getAvailable = (item) => {
    if (!dateFrom || !dateTo) return item.quantity_available;
    return availability[item.id]?.available ?? item.quantity_available;
  };

  const categories = ['all', ...new Set(items.map(i => i.category).filter(Boolean))];

  const filtered = items.filter(i => {
    if (catFilter !== 'all' && i.category !== catFilter) return false;
    if (search && !i.name.toLowerCase().includes(search.toLowerCase()) &&
        !(i.description||'').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }).sort((a, b) => {
    if (sortBy === 'price_asc')  return Number(a.price_day||0) - Number(b.price_day||0);
    if (sortBy === 'price_desc') return Number(b.price_day||0) - Number(a.price_day||0);
    if (sortBy === 'name')       return a.name.localeCompare(b.name);
    if (sortBy === 'available')  return getAvailable(b) - getAvailable(a);
    return 0;
  });

  // Auto-calc miles for checkout form when delivery address changes
  React.useEffect(() => {
    if (form.fulfillment_type !== 'delivery' || !form.delivery_address || form.delivery_address.length < 8) {
      setForm(p => ({ ...p, delivery_miles: 0 }));
      return;
    }
    const t = setTimeout(async () => {
      setDistLoading(true);
      setDistError('');
      try {
        const r = await axios.get(`${API}/api/rentals/distance?destination=${encodeURIComponent(form.delivery_address)}`);
        setForm(p => ({ ...p, delivery_miles: r.data.miles_one_way || 0 }));
      } catch(e) {
        setForm(p => ({ ...p, delivery_miles: 0 }));
        setDistError(e.response?.data?.error || 'Address not found — please enter a valid address.');
      }
      setDistLoading(false);
    }, 700);
    return () => clearTimeout(t);
  }, [form.delivery_address, form.fulfillment_type]);

  // Check availability for cart items when dates change
  React.useEffect(() => {
    if (!form.from_date || !form.to_date || form.from_date >= form.to_date || cart.length === 0) {
      setCartConflicts({});
      return;
    }
    axios.get(`${API}/api/public/rentals/availability?from=${form.from_date}&to=${form.to_date}`)
      .then(r => {
        const avail = r.data.availability || {};
        const conflicts = {};
        cart.forEach(item => {
          const a = avail[item.id];
          if (a !== undefined && a.available < 1) conflicts[item.id] = true;
        });
        setCartConflicts(conflicts);
      }).catch(() => {});
  }, [form.from_date, form.to_date, cart.length]);

  const addToCart    = (item) => { if (!cart.find(c => c.id === item.id)) setCart(c => [...c, {...item, qty:1}]); };
  const removeFromCart = (id) => setCart(c => c.filter(x => x.id !== id));
  const inCart       = (id)  => cart.some(c => c.id === id);

  const days  = form.from_date && form.to_date ? daysBetween(form.from_date, form.to_date) : 1;

  // ── Server-side price. Debounced on anything that changes it. ──
  const [price, setPrice]           = useState(null);   // last /price response
  const [priceError, setPriceError] = useState('');
  const [priceLoading, setPriceLoading] = useState(false);
  const priceTimer = useRef(null);
  const priceInputs = JSON.stringify({
    from_date: form.from_date, to_date: form.to_date, fulfillment_type: form.fulfillment_type,
    delivery_miles: form.fulfillment_type === 'delivery' ? Number(form.delivery_miles || 0) : 0,
    items: cart.map(i => ({ rental_item_id: i.id, quantity: i.qty || 1 })),
    portal: !!portalToken,
  });
  useEffect(() => {
    const body = JSON.parse(priceInputs);
    if (!body.from_date || !body.to_date || body.to_date < body.from_date || !body.items.length) { setPrice(null); setPriceError(''); return; }
    clearTimeout(priceTimer.current);
    priceTimer.current = setTimeout(async () => {
      setPriceLoading(true);
      try {
        const r = await axios.post(`${API}/api/public/rentals/price`, { ...body, portal_token: portalToken || undefined },
          portalToken ? { headers: { 'x-portal-token': portalToken } } : {});
        setPrice(r.data); setPriceError('');
      } catch (e) {
        setPrice(null); setPriceError(e.response?.data?.error || 'Could not price this booking.');
      }
      setPriceLoading(false);
    }, 350);
    return () => clearTimeout(priceTimer.current);
  }, [priceInputs, portalToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const lineTotal = (item) => price?.lines?.find(l => l.rental_item_id === item.id)?.total_price;
  const mileageCharge = Number(price?.delivery_fee || 0);
  const discountPct   = Number(price?.discount_percent || 0);
  const discountAmount = Number(price?.discount_amount || 0);
  const checkoutTotal = Number(price?.total || 0);
  const depositDue    = Number(price?.deposit_amount || 0);

  // Portal sign-in helpers
  const sendPortalCode = async () => {
    if (!portalEmail.trim()) return;
    setPortalStep('sending'); setPortalError('');
    try {
      await axios.post(`${API}/api/rental-portal/send-code`, { email: portalEmail, subdomain: SUBDOMAIN });
      setPortalStep('code');
    } catch(e) {
      setPortalError(e.response?.data?.error || 'Email not found. Contact the business to set up your account.');
      setPortalStep('idle');
    }
  };
  const verifyPortalCode = async () => {
    if (!portalCode.trim()) return;
    setPortalError('');
    try {
      const res = await axios.post(`${API}/api/rental-portal/verify-code`, { email: portalEmail, code: portalCode, subdomain: SUBDOMAIN });
      setPortalClient(res.data.client);
      setPortalToken(res.data.token);
      setPortalStep('done');
      // Pre-fill contact form
      setForm(f => ({ ...f, client_name: res.data.client.name || f.client_name, client_email: res.data.client.email || f.client_email, client_phone: res.data.client.phone || f.client_phone, client_company: res.data.client.company || f.client_company }));
    } catch(e) {
      setPortalError(e.response?.data?.error || 'Invalid code. Please try again.');
    }
  };

  // Sync date picker to cart form
  useEffect(() => {
    if (dateFrom) setForm(p => ({...p, from_date: dateFrom}));
    if (dateTo)   setForm(p => ({...p, to_date: dateTo}));
  }, [dateFrom, dateTo]);

  const submitRequest = async () => {
    if (!form.from_date || !form.to_date) return alert('Please select pickup and return dates.');
    if (!cart.length) return alert('Add at least one item.');
    if (!price) return alert('Please wait for the price to load.');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/public/rentals/orders`, {
        price_version: 2,
        expected_total: price.total,
        from_date: form.from_date, to_date: form.to_date,
        fulfillment_type: form.fulfillment_type, notes: form.notes,
        client_name: form.client_name, client_email: form.client_email, client_phone: form.client_phone || null, client_company: form.client_company || null,
        delivery_address: form.fulfillment_type === 'delivery' ? form.delivery_address : null,
        delivery_miles: form.fulfillment_type === 'delivery' ? Number(form.delivery_miles || 0) : 0,
        portal_token: portalToken || undefined,
        items: cart.map(i => ({ rental_item_id: i.id, item_name: i.name, quantity: i.qty || 1 })),
      }, portalToken ? { headers: { 'x-portal-token': portalToken } } : {});
      const order = toContractOrder(res.data.order);
      // Remember it: closing the modal must not strand the customer or the unit.
      const p = { token: order.public_token, order_number: order.order_number, hold_expires_at: order.hold_expires_at };
      writePending(p); setPending(p);
      setContractOrder(order);
      setStep('contract');
    } catch(e) {
      if (e.response?.status === 409 && e.response?.data?.code === 'price_changed') {
        setPrice(p => p ? { ...p, total: e.response.data.expected_total } : p);
        alert('The price changed while you were booking — please review the updated total and try again.');
      } else {
        alert('Error: ' + (e.response?.data?.error || e.message));
      }
    }
    setSubmitting(false);
  };

  const submitQuoteRequest = async () => {
    if (!quoteForm.name || !quoteForm.email) return alert('Name and email are required.');
    setQuoteSubmitting(true);
    try {
      await axios.post(`${API}/api/public/rentals/quote-request`, {
        name: quoteForm.name, email: quoteForm.email, phone: quoteForm.phone,
        message: quoteForm.message, from_date: quoteForm.from_date, to_date: quoteForm.to_date,
        fulfillment_type: quoteForm.fulfillment_type,
        company: quoteForm.company || '',
        delivery_address: quoteForm.delivery_address || '',
        items: cart.map(i => ({ id: i.id, name: i.name, price_day: i.price_day, qty: i.qty || 1 })),
      });
      setQuoteSuccess(true);
    } catch(e) { alert('Error submitting request: ' + (e.response?.data?.error || e.message)); }
    setQuoteSubmitting(false);
  };

  const S = {
    page: { fontFamily:"'DM Sans',Arial,sans-serif", background:'#0a0a0f', minHeight:'100vh', color:'#e2e8f0' },
    hero: { background:'linear-gradient(135deg,#0f0f1a,#1a0a00)', padding:'48px 24px 32px', textAlign:'center', borderBottom:'1px solid #2e3150' },
    container: { maxWidth:1140, margin:'0 auto', padding:'0 20px' },
    badge: { display:'inline-block', background:'#f9731620', color:'#f97316', fontSize:11, fontWeight:700, padding:'4px 12px', borderRadius:20, marginBottom:12 },
    h1: { fontSize:36, fontWeight:800, color:'#fff', margin:'0 0 8px', letterSpacing:-1 },
    sub: { fontSize:15, color:'#94a3b8', margin:'0 0 24px' },
    input: { background:'#252838', border:'1px solid #2e3150', borderRadius:8, padding:'10px 14px', color:'#e2e8f0', fontSize:13, outline:'none', width:'100%', boxSizing:'border-box' },
    btn: (v) => ({ padding:v==='lg'?'14px 28px':'10px 20px', borderRadius:8, border:'none', fontWeight:700, fontSize:v==='lg'?15:13, cursor:'pointer',
      background:v==='danger'?'#7f1d1d':v==='ghost'?'transparent':'#f97316', color:v==='ghost'?'#94a3b8':'#fff' }),
    card: { background:'#252838', border:'1px solid #2e3150', borderRadius:12, overflow:'hidden' },
  };

  if (step === 'success') return (
    <div style={{...S.page, display:'flex', alignItems:'center', justifyContent:'center'}}>
      <div style={{textAlign:'center', padding:40, maxWidth:480}}>
        <div style={{fontSize:72, marginBottom:16}}>✅</div>
        <div style={{fontSize:28, fontWeight:800, color:'#fff', marginBottom:8}}>You're All Set!</div>
        <div style={{fontSize:15, color:'#94a3b8', marginBottom:8, lineHeight:1.6}}>
          Your deposit was received and your rental is confirmed.{successOrderId && <> Order <span style={{color:'#f97316'}}>{successOrderId}</span></>}
        </div>
        <div style={{fontSize:13, color:'#94a3b8', marginBottom:6}}>📧 Signed contract sent to your email.</div>
        {business.phone && <div style={{fontSize:13, color:'#f97316', marginBottom:32, fontWeight:600}}>📞 Questions? Call or text {business.phone}</div>}
        <button style={S.btn('lg')} onClick={() => { setStep('browse'); setCart([]); setForm({from_date:'',to_date:'',fulfillment_type:'pickup',notes:''}); setContractOrder(null); setSuccessOrderId(null); }}>Browse More Equipment</button>
      </div>
    </div>
  );

  return (
    <div style={S.page}>
      {/* Hero */}
      <div style={S.hero}>
        <div style={S.container}>
          <div style={S.badge}>🏗️ Equipment Rental{bizPlace ? ` • ${bizPlace}` : ''}</div>
          <h1 style={S.h1}>{business.name}</h1>
          <p style={S.sub}>Professional concrete & flooring equipment. Daily, weekly & monthly rates.</p>
          <div style={{display:'flex', gap:12, justifyContent:'center', marginBottom:20}}>
            <button onClick={()=>setStep('browse')} style={{...S.btn(''), background:'#f97316', color:'#fff', padding:'12px 28px', fontSize:14}}>🛒 Browse Equipment</button>
            <button onClick={()=>{ setCart([]); setStep('quote-request'); }} style={{...S.btn(''), background:'transparent', color:'#f97316', border:'2px solid #f97316', padding:'12px 28px', fontSize:14}}>📋 Request a Quote</button>
          </div>
          <div style={{display:'flex', gap:24, justifyContent:'center', flexWrap:'wrap'}}>
            {[['📦','Same-Day Pickup'],['🚚','Delivery Available'],['🔧','All Equipment Serviced'],['📞','Local Support']].map(([icon,txt])=>(
              <div key={txt} style={{fontSize:13, color:'#94a3b8'}}>{icon} {txt}</div>
            ))}
          </div>
        </div>

      {pending && step !== 'contract' && step !== 'success' && (
        <div data-testid="pending-banner" style={{background:'#1e2030', borderBottom:'1px solid #f97316', padding:'12px 20px'}}>
          <div style={{...S.container, display:'flex', alignItems:'center', gap:12, flexWrap:'wrap'}}>
            <span style={{fontSize:13, color:'#e2e8f0'}}>⏱ You have a booking in progress — order <strong style={{color:'#f97316'}}>{pending.order_number}</strong>, equipment held until <strong>{holdUntilText(pending.hold_expires_at)}</strong>.</span>
            <div style={{marginLeft:'auto', display:'flex', gap:8}}>
              <button disabled={pendingBusy} onClick={resumePending} style={{...S.btn(''), padding:'8px 16px'}}>Continue booking</button>
              <button disabled={pendingBusy} onClick={() => cancelPending()} style={{...S.btn('ghost'), padding:'8px 12px', border:'1px solid #2e3150'}}>Cancel booking</button>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Browse */}
      {step === 'browse' && (
        <div style={{...S.container, padding:'28px 20px'}}>
          <div style={{display:'flex', gap:16, alignItems:'flex-start'}}>

            {/* ── LEFT: filters + grid ── */}
            <div style={{flex:1, minWidth:0}}>

            {/* Portal Sign-In Banner */}
            {portalStep !== 'done' ? (
              <div style={{background:'#1e2235',border:'1px solid #2e3150',borderRadius:12,padding:'14px 18px',marginBottom:16}}>
                <div style={{fontSize:11,fontWeight:700,color:'#f97316',textTransform:'uppercase',letterSpacing:'.5px',marginBottom:8}}>🏷 Have an Account? Sign In for Your Discount</div>
                {portalStep === 'idle' && (
                  <div style={{display:'flex',gap:8}}>
                    <input style={{...S.input,flex:1,fontSize:12}} type="email" placeholder="your@email.com" value={portalEmail} onChange={e=>setPortalEmail(e.target.value)} onKeyDown={e=>e.key==='Enter'&&sendPortalCode()} />
                    <button style={{...S.btn(''),padding:'9px 16px',fontSize:12,flexShrink:0}} onClick={sendPortalCode}>Send Code</button>
                  </div>
                )}
                {portalStep === 'sending' && <div style={{fontSize:12,color:'#94a3b8'}}>Sending code…</div>}
                {portalStep === 'code' && (
                  <div>
                    <div style={{fontSize:12,color:'#94a3b8',marginBottom:8}}>Check your email for a 6-digit code:</div>
                    <div style={{display:'flex',gap:8}}>
                      <input style={{...S.input,flex:1,fontSize:14,letterSpacing:4,fontWeight:700}} placeholder="000000" maxLength={6} value={portalCode} onChange={e=>setPortalCode(e.target.value)} onKeyDown={e=>e.key==='Enter'&&verifyPortalCode()} />
                      <button style={{...S.btn(''),padding:'9px 16px',fontSize:12,flexShrink:0}} onClick={verifyPortalCode}>Verify</button>
                    </div>
                  </div>
                )}
                {portalError && <div style={{fontSize:11,color:'#f87171',marginTop:6}}>{portalError}</div>}
              </div>
            ) : (
              <div style={{background:'#0c1a0c',border:'1px solid #14532d',borderRadius:12,padding:'12px 18px',marginBottom:16,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:'#4ade80'}}>✓ Signed in as {portalClient?.name}</div>
                  {discountPct > 0
                    ? <div style={{fontSize:12,color:'#86efac',marginTop:2}}>🏷 <strong>{discountPct}%</strong> account discount applied to your order</div>
                    : <div style={{fontSize:12,color:'#94a3b8',marginTop:2}}>No discount on this account</div>}
                </div>
                <button onClick={()=>{setPortalStep('idle');setPortalClient(null);setPortalToken(null);setPortalEmail('');setPortalCode('');}} style={{fontSize:11,color:'#94a3b8',background:'none',border:'none',cursor:'pointer'}}>Sign out</button>
              </div>
            )}

              {/* Date availability bar */}
              <div style={{background:'#252838', border:'1px solid #2e3150', borderRadius:12, padding:'16px 20px', marginBottom:16}}>
                <div style={{fontSize:12, fontWeight:700, color:'#f97316', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:10}}>
                  🗓️ Check Availability for Your Dates
                </div>
                <div style={{display:'flex', gap:10, flexWrap:'wrap', alignItems:'center'}}>
                  <div style={{flex:1, minWidth:140}}>
                    <label style={{fontSize:10, color:'#94a3b8', display:'block', marginBottom:3}}>Pickup Date</label>
                    <input type="date" style={{...S.input, fontSize:13}} value={dateFrom}
                      min={new Date().toISOString().slice(0,10)}
                      onChange={e => setDateFrom(e.target.value)} />
                  </div>
                  <div style={{flex:1, minWidth:140}}>
                    <label style={{fontSize:10, color:'#94a3b8', display:'block', marginBottom:3}}>Return Date</label>
                    <input type="date" style={{...S.input, fontSize:13}} value={dateTo}
                      min={dateFrom || new Date().toISOString().slice(0,10)}
                      onChange={e => setDateTo(e.target.value)} />
                  </div>
                  {(dateFrom || dateTo) && (
                    <button onClick={() => { setDateFrom(''); setDateTo(''); setAvailability({}); }}
                      style={{padding:'9px 14px', borderRadius:8, border:'1px solid #2e3150', background:'transparent', color:'#94a3b8', fontSize:12, cursor:'pointer', whiteSpace:'nowrap', marginTop:16}}>
                      Clear
                    </button>
                  )}
                  {dateFrom && dateTo && dateFrom < dateTo && (
                    <div style={{marginTop:16, fontSize:12, color: availLoading ? '#94a3b8' : '#34d399', whiteSpace:'nowrap'}}>
                      {availLoading ? '⏳ Checking...' : `✓ Showing availability for ${daysBetween(dateFrom, dateTo)} days`}
                    </div>
                  )}
                </div>
              </div>

              {/* Search + filters row */}
              <div style={{display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center'}}>
                <input placeholder="🔍 Search equipment..." style={{...S.input, width:220, flex:'0 0 auto'}}
                  value={search} onChange={e => setSearch(e.target.value)} />
                <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                  style={{...S.input, width:'auto', flex:'0 0 auto', padding:'10px 12px'}}>
                  <option value="default">Sort: Default</option>
                  <option value="price_asc">Price: Low → High</option>
                  <option value="price_desc">Price: High → Low</option>
                  <option value="name">Name A→Z</option>
                  <option value="available">Availability</option>
                </select>
                <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
                  {categories.map(c => (
                    <button key={c} onClick={() => setCatFilter(c)}
                      style={{...S.btn(''), fontSize:11, padding:'7px 14px',
                        background: catFilter===c ? '#f97316' : '#252838',
                        border: '1px solid ' + (catFilter===c ? '#f97316' : '#2e3150'),
                        textTransform:'capitalize'}}>
                      {c === 'all' ? `All (${items.length})` : `${c} (${items.filter(i=>i.category===c).length})`}
                    </button>
                  ))}
                </div>
              </div>

              {loading && <div style={{textAlign:'center', padding:60, color:'#94a3b8'}}>Loading equipment...</div>}

              <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(270px,1fr))', gap:16}}>
                {filtered.map(item => {
                  const avail = getAvailable(item);
                  const hasDateFilter = !!(dateFrom && dateTo && dateFrom < dateTo);
                  const isAvail = avail > 0;
                  const already = inCart(item.id);
                  return (
                    <div key={item.id} style={{...S.card, opacity: hasDateFilter && !isAvail ? 0.55 : 1, transition:'opacity 0.2s'}}>
                      <div style={{height:160, background:'#1e2030', position:'relative', display:'flex', alignItems:'center', justifyContent:'center'}}>
                        {item.img_url
                          ? <img src={item.img_url} alt={item.name} style={{width:'100%',height:'100%',objectFit:'cover'}} onError={e=>e.target.style.display='none'} />
                          : <div style={{fontSize:48}}>🔧</div>}
                        {/* Availability badge */}
                        <div style={{position:'absolute', top:8, right:8}}>
                          {hasDateFilter ? (
                            <span style={{background: isAvail ? 'rgba(52,211,153,0.9)' : 'rgba(248,113,113,0.9)', color:'#0a0b12', fontSize:10, fontWeight:800, padding:'3px 10px', borderRadius:20}}>
                              {isAvail ? `${avail} Available` : 'Booked'}
                            </span>
                          ) : (
                            <span style={{background: item.quantity_available>0 ? 'rgba(52,211,153,0.9)' : 'rgba(248,113,113,0.9)', color:'#0a0b12', fontSize:10, fontWeight:800, padding:'3px 10px', borderRadius:20}}>
                              {item.quantity_available>0 ? `${item.quantity_available} in fleet` : 'Unavailable'}
                            </span>
                          )}
                        </div>
                        {item.category && (
                          <div style={{position:'absolute', top:8, left:8}}>
                            <span style={{background:'#f9731625', color:'#f97316', fontSize:10, fontWeight:700, padding:'2px 9px', borderRadius:20, textTransform:'capitalize'}}>{item.category}</span>
                          </div>
                        )}
                      </div>
                      <div style={{padding:16}}>
                        <div style={{fontSize:14, fontWeight:700, color:'#f1f5f9', marginBottom:4}}>{item.name}</div>
                        {item.description && <div style={{fontSize:11, color:'#94a3b8', marginBottom:12, lineHeight:1.5}}>{item.description}</div>}
                        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:6, marginBottom:12}}>
                          {[['Day',item.price_day,'#f97316'],['Week',item.price_week,'#60a5fa'],['Month',item.price_month,'#a78bfa']].map(([l,p,c])=>(
                            <div key={l} style={{background:'#1e2030', borderRadius:6, padding:'6px 4px', textAlign:'center'}}>
                              <div style={{fontSize:9, color:'#94a3b8', marginBottom:1}}>{l}</div>
                              <div style={{fontSize:13, fontWeight:800, color: p ? c : '#3a3f60'}}>{p ? fmt(p) : '—'}</div>
                            </div>
                          ))}
                        </div>
                        {/* Per-item estimates come from the cart's server price, not from a browser-side copy of the rate rules. */}
                        <button
                          style={{...S.btn(''), width:'100%',
                            background: already ? '#14532d' : (!hasDateFilter || isAvail) && item.quantity_available>0 ? '#f97316' : '#2e3150',
                            color: already ? '#34d399' : '#fff',
                            cursor: (!hasDateFilter || isAvail) && item.quantity_available>0 ? 'pointer' : 'not-allowed'}}
                          onClick={() => { if ((!hasDateFilter || isAvail) && item.quantity_available>0) addToCart(item); }}
                          disabled={hasDateFilter && !isAvail || item.quantity_available===0}>
                          {already ? '✓ In Cart' : hasDateFilter && !isAvail ? 'Not Available' : item.quantity_available>0 ? '+ Add to Cart' : 'Unavailable'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {!filtered.length && !loading && (
                <div style={{textAlign:'center', padding:60, color:'#94a3b8'}}>
                  <div style={{fontSize:32, marginBottom:12}}>🔍</div>
                  <div>No equipment matches your filters.</div>
                  {catFilter !== 'all' && <button onClick={()=>setCatFilter('all')} style={{marginTop:10, color:'#f97316', background:'none', border:'none', cursor:'pointer', fontSize:13}}>Clear filters</button>}
                </div>
              )}
            </div>

            {/* ── RIGHT: sticky cart sidebar ── */}
            <div style={{width:280, flexShrink:0, position:'sticky', top:20, alignSelf:'flex-start'}}>
              <div style={{background:'#252838', border:'1px solid #2e3150', borderRadius:12, overflow:'hidden'}}>
                <div style={{padding:'14px 16px', borderBottom:'1px solid #2e3150', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                  <div style={{fontWeight:800, fontSize:14, color:'#e2e8f0'}}>🛒 Cart {cart.length > 0 && <span style={{background:'#f97316', color:'#fff', fontSize:11, borderRadius:20, padding:'1px 7px', marginLeft:4}}>{cart.length}</span>}</div>
                  {cart.length > 0 && <button onClick={()=>setCart([])} style={{background:'none', border:'none', color:'#94a3b8', fontSize:11, cursor:'pointer'}}>Clear</button>}
                </div>
                {cart.length === 0 ? (
                  <div style={{padding:'28px 16px', textAlign:'center', color:'#94a3b8', fontSize:12}}>
                    <div style={{fontSize:28, marginBottom:8}}>📦</div>
                    Add equipment to start your request
                  </div>
                ) : (
                  <div style={{padding:'10px 12px'}}>
                    {cart.map(item => (
                      <div key={item.id} style={{display:'flex', alignItems:'center', gap:8, padding:'8px 0', borderBottom:'1px solid #1e2030'}}>
                        <div style={{flex:1, minWidth:0}}>
                          <div style={{fontSize:12, fontWeight:700, color:'#e2e8f0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{item.name}</div>
                          <div style={{fontSize:11, color:'#f97316'}}>{fmt(item.price_day)}/day</div>
                        </div>
                        <button onClick={()=>removeFromCart(item.id)} style={{background:'none', border:'none', color:'#94a3b8', cursor:'pointer', fontSize:14, lineHeight:1, flexShrink:0}}>✕</button>
                      </div>
                    ))}
                    {/* Date fields in sidebar */}
                    <div style={{marginTop:12, paddingTop:10, borderTop:'1px solid #2e3150'}}>
                      <label style={{fontSize:10, color:'#94a3b8', display:'block', marginBottom:3}}>Pickup</label>
                      <input type="date" style={{...S.input, marginBottom:6, fontSize:12, padding:'8px 10px'}}
                        value={dateFrom} min={new Date().toISOString().slice(0,10)}
                        onChange={e => { setDateFrom(e.target.value); setForm(p=>({...p,from_date:e.target.value})); }} />
                      <label style={{fontSize:10, color:'#94a3b8', display:'block', marginBottom:3}}>Return</label>
                      <input type="date" style={{...S.input, marginBottom:8, fontSize:12, padding:'8px 10px'}}
                        value={dateTo} min={dateFrom || new Date().toISOString().slice(0,10)}
                        onChange={e => { setDateTo(e.target.value); setForm(p=>({...p,to_date:e.target.value})); }} />
                      {form.from_date && form.to_date && form.from_date < form.to_date && (
                        <div style={{fontSize:12, color:'#94a3b8', marginBottom:8, textAlign:'center'}}>
                          {days} day{days!==1?'s':''} · <span style={{color:'#f97316', fontWeight:800}}>{price ? `Est. ${fmtFull(checkoutTotal)}` : (priceLoading ? 'Pricing…' : 'Est. —')}</span>
                        </div>
                      )}
                    </div>
                    <div style={{marginBottom:6}}>
                      <select style={{...S.input, fontSize:12, padding:'8px 10px', marginBottom:6}} value={form.fulfillment_type} onChange={e=>setForm(p=>({...p,fulfillment_type:e.target.value}))}>
                        <option value="pickup">📦 Pickup</option>
                        <option value="delivery">🚚 Delivery (+fee)</option>
                      </select>
                    </div>
                    <button style={{...S.btn(''), width:'100%', fontSize:13, padding:'11px', opacity:submitting?0.6:1}}
                      onClick={submitRequest}
                      disabled={submitting || !form.from_date || !form.to_date}>
                      {submitting ? '⏳ Creating...' : '📋 Continue →'}
                    </button>
                    <div style={{textAlign:'center', marginTop:8}}>
                      <button onClick={()=>setStep('quote-request')}
                        style={{fontSize:11, color:'#94a3b8', background:'none', border:'none', cursor:'pointer'}}>
                        Request quote instead
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Cart step (mobile flow) */}
      {step === 'cart' && (
        <div style={{...S.container, padding:'28px 20px', maxWidth:640}}>
          <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:24}}>
            <button style={S.btn('ghost')} onClick={()=>setStep('browse')}>← Back</button>
            <div style={{fontSize:20, fontWeight:800, color:'#fff'}}>Your Request ({cart.length} items)</div>
          </div>
          {cart.map(item=>(
            <div key={item.id} style={{...S.card, display:'flex', alignItems:'center', gap:16, padding:16, marginBottom:10, border: cartConflicts[item.id] ? '1px solid rgba(248,113,113,0.5)' : undefined}}>
              <div style={{fontSize:32}}>🔧</div>
              <div style={{flex:1}}>
                <div style={{fontSize:14, fontWeight:700, color:'#f1f5f9'}}>{item.name}</div>
                <div style={{fontSize:12, color:'#f97316'}}>{fmt(item.price_day)}/day</div>
                {cartConflicts[item.id] && <div style={{fontSize:11, color:'#f87171', marginTop:3, fontWeight:600}}>⚠️ Not available for selected dates</div>}
              </div>
              <button style={{...S.btn('danger'), fontSize:11, padding:'4px 10px'}} onClick={()=>removeFromCart(item.id)}>Remove</button>
            </div>
          ))}
          {Object.keys(cartConflicts).length > 0 && (
            <div style={{padding:'10px 14px', background:'rgba(248,113,113,0.08)', border:'1px solid rgba(248,113,113,0.2)', borderRadius:8, marginBottom:12, fontSize:12, color:'#f87171'}}>
              ⚠️ Some items are already booked for these dates. Remove them or choose different dates.
            </div>
          )}
          <div style={{...S.card, padding:20, marginTop:20}}>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12}}>
              <div><label style={{fontSize:11, color:'#94a3b8', display:'block', marginBottom:4}}>Pickup Date *</label>
                <input type="date" style={S.input} value={form.from_date} onChange={e=>setForm(p=>({...p,from_date:e.target.value}))} /></div>
              <div><label style={{fontSize:11, color:'#94a3b8', display:'block', marginBottom:4}}>Return Date *</label>
                <input type="date" style={S.input} value={form.to_date} onChange={e=>setForm(p=>({...p,to_date:e.target.value}))} /></div>
            </div>
            <div style={{marginBottom:12}}>
              <label style={{fontSize:11, color:'#94a3b8', display:'block', marginBottom:4}}>Fulfillment</label>
              <select style={S.input} value={form.fulfillment_type} onChange={e=>setForm(p=>({...p,fulfillment_type:e.target.value}))}>
                <option value="pickup">Pickup</option>
                <option value="delivery">Delivery (+fee)</option>
              </select>
            </div>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12}}>
              <div><label style={{fontSize:11, color:'#94a3b8', display:'block', marginBottom:4}}>Your Name *</label>
                <input style={S.input} placeholder="John Smith" value={form.client_name} onChange={e=>setForm(p=>({...p,client_name:e.target.value}))} /></div>
              <div><label style={{fontSize:11, color:'#94a3b8', display:'block', marginBottom:4}}>Company / Business</label>
                <input style={S.input} placeholder="ABC Contractors LLC" value={form.client_company} onChange={e=>setForm(p=>({...p,client_company:e.target.value}))} /></div>
              <div><label style={{fontSize:11, color:'#94a3b8', display:'block', marginBottom:4}}>Email *</label>
                <input style={S.input} type="email" placeholder="john@company.com" value={form.client_email} onChange={e=>setForm(p=>({...p,client_email:e.target.value}))} /></div>
              <div><label style={{fontSize:11, color:'#94a3b8', display:'block', marginBottom:4}}>Phone</label>
                <input style={S.input} placeholder="(702) 555-0000" value={form.client_phone} onChange={e=>setForm(p=>({...p,client_phone:e.target.value}))} /></div>
            </div>
            {form.fulfillment_type === 'delivery' && (
              <div style={{marginBottom:12}}>
                <label style={{fontSize:11, color:'#94a3b8', display:'block', marginBottom:4}}>Delivery Address <span style={{color:'#f97316'}}>*</span></label>
                <input style={S.input} placeholder={`1234 Job Site Ave${bizPlace ? `, ${bizPlace}` : ""}`} value={form.delivery_address} onChange={e=>setForm(p=>({...p,delivery_address:e.target.value}))} />
                {form.delivery_miles > 0 && !distLoading && (
                  <div style={{marginTop:6, padding:'8px 12px', background:'rgba(249,115,22,0.08)', border:'1px solid rgba(249,115,22,0.2)', borderRadius:7, fontSize:12, display:'flex', justifyContent:'space-between'}}>
                    <span style={{color:'#94a3b8'}}>🚚 {(form.delivery_miles*2).toFixed(1)} mi round trip</span>
                    <span style={{color:'#f97316', fontWeight:700}}>{mileageCharge > 0 ? `+${fmt(mileageCharge)} delivery` : 'FREE delivery'}</span>
                  </div>
                )}
                {distLoading && <div style={{fontSize:11, color:'#94a3b8', marginTop:4}}>📍 Validating address...</div>}
                {distError && !distLoading && <div style={{fontSize:11, color:'#f87171', marginTop:4}}>⚠️ {distError}</div>}
              </div>
            )}
            <div style={{marginBottom:12}}>
              <label style={{fontSize:11, color:'#94a3b8', display:'block', marginBottom:4}}>Job Notes</label>
              <textarea style={{...S.input, height:60, resize:'none'}} placeholder="Project details, job site..." value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} />
            </div>
            {form.from_date && form.to_date && (
              <div style={{background:'#1e2030', borderRadius:8, padding:12, marginBottom:12, fontSize:13}}>
                <div style={{display:'flex', justifyContent:'space-between', marginBottom:4}}>
                  <span style={{color:'#94a3b8'}}>Duration</span><span>{days} day{days!==1?'s':''}</span>
                </div>
                {cart.map(i=>(
                  <div key={i.id} style={{display:'flex',justifyContent:'space-between',color:'#94a3b8',marginBottom:3,fontSize:12}}>
                    <span>{i.name}{(() => { const l = price?.lines?.find(x => x.rental_item_id === i.id); return l ? <span style={{color:'#64748b'}}> · {describeRateLine(l, days)}</span> : null; })()}</span><span>{lineTotal(i) !== undefined ? fmtFull(lineTotal(i)) : '…'}</span>
                  </div>
                ))}
                {mileageCharge > 0 && (
                  <div style={{display:'flex',justifyContent:'space-between',color:'#f97316',marginBottom:3,fontSize:12}}>
                    <span>🚚 Delivery ({(form.delivery_miles*2).toFixed(1)} mi)</span><span>+{fmtFull(mileageCharge)}</span>
                  </div>
                )}
                {discountAmount > 0 && (
                  <div style={{display:'flex',justifyContent:'space-between',color:'#4ade80',marginBottom:3,fontSize:12,fontWeight:700}}>
                    <span>🏷 Account Discount ({discountPct}%)</span><span>−{fmtFull(discountAmount)}</span>
                  </div>
                )}
                {price?.tax_exempt ? (
                  <div style={{display:'flex',justifyContent:'space-between',color:'#94a3b8',marginBottom:3,fontSize:12}}>
                    <span>Tax</span><span>Exempt</span>
                  </div>
                ) : (price?.tax_breakdown || []).map((p, idx) => (
                  <div key={idx} style={{display:'flex',justifyContent:'space-between',color:'#94a3b8',marginBottom:3,fontSize:12}}>
                    <span>{p.name} ({Number(p.rate)}%)</span><span>{fmtFull(p.amount)}</span>
                  </div>
                ))}
                <div style={{display:'flex',justifyContent:'space-between',fontSize:16,fontWeight:800,color:'#f97316',borderTop:'1px solid #2e3150',paddingTop:8,marginTop:4}}>
                  <span>Total</span><span>{price ? fmtFull(checkoutTotal) : (priceLoading ? '…' : '—')}</span>
                </div>
                {price && (
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:12,color:'#60a5fa',marginTop:4}}>
                    <span>Deposit due today</span><span>{fmtFull(depositDue)}</span>
                  </div>
                )}
                {priceError && <div style={{fontSize:11, color:'#f87171', marginTop:6}}>⚠️ {priceError}</div>}
              </div>
            )}
            <button style={{...S.btn('lg'), width:'100%', opacity:submitting?0.6:1}} onClick={submitRequest} disabled={submitting||!price||priceLoading||!form.from_date||!form.to_date||!form.client_name||!form.client_email||(form.fulfillment_type==='delivery'&&!form.delivery_address)||!!distError||Object.keys(cartConflicts).length>0}>
              {submitting?'⏳ Creating Order...':'📋 Continue to Sign & Pay →'}
            </button>
            <div style={{fontSize:11, color:'#94a3b8', textAlign:'center', marginTop:10}}>Next: sign rental agreement & pay {price ? `the ${fmtFull(depositDue)} deposit` : 'your deposit'} via Stripe. Your equipment is held for {settings.hold_minutes || 30} minutes while you do.</div>
            <div style={{textAlign:'center', marginTop:12}}>
              <span style={{fontSize:12, color:'#94a3b8'}}>Not ready to pay? </span>
              <button onClick={()=>setStep('quote-request')} style={{fontSize:12, color:'#f97316', fontWeight:700, background:'none', border:'none', cursor:'pointer'}}>Request a quote instead →</button>
            </div>
          </div>
        </div>
      )}

      {/* Quote Request Form */}
      {step === 'quote-request' && (
        <div style={{...S.container, padding:'40px 20px', maxWidth:600}}>
          {quoteSuccess ? (
            <div style={{textAlign:'center', padding:'60px 20px'}}>
              <div style={{fontSize:64, marginBottom:16}}>📬</div>
              <div style={{fontSize:26, fontWeight:800, color:'#fff', marginBottom:10}}>Quote Request Sent!</div>
              <div style={{fontSize:15, color:'#94a3b8', marginBottom:8, lineHeight:1.7}}>
                We'll review your request and send a custom quote to <span style={{color:'#f97316'}}>{quoteForm.email}</span> within 1 business day.
              </div>
              <div style={{fontSize:13, color:'#94a3b8', marginBottom:32}}>📞 Need it faster? Call or text us directly.</div>
              <button style={S.btn('lg')} onClick={()=>{ setStep('browse'); setQuoteSuccess(false); setQuoteForm({name:'',email:'',phone:'',message:'',from_date:'',to_date:'',fulfillment_type:'pickup',delivery_address:''}); }}>Browse Equipment</button>
            </div>
          ) : (
            <div>
              <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:28}}>
                <button style={S.btn('ghost')} onClick={()=>setStep('browse')}>← Back</button>
                <div>
                  <div style={{fontSize:22, fontWeight:800, color:'#fff'}}>Request a Quote</div>
                  <div style={{fontSize:13, color:'#94a3b8'}}>Tell us what you need — we'll send a custom price within 1 business day</div>
                </div>
              </div>
              {cart.length > 0 && (
                <div style={{...S.card, padding:16, marginBottom:20}}>
                  <div style={{fontSize:12, fontWeight:700, color:'#f97316', marginBottom:10, textTransform:'uppercase', letterSpacing:'.5px'}}>Selected Equipment ({cart.length})</div>
                  {cart.map(i=>(
                    <div key={i.id} style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 0', borderBottom:'1px solid #2e3150', fontSize:13}}>
                      <span style={{color:'#e2e8f0'}}>{i.name}</span>
                      <div style={{display:'flex', gap:10, alignItems:'center'}}>
                        <span style={{color:'#f97316'}}>{fmt(i.price_day)}/day</span>
                        <button onClick={()=>removeFromCart(i.id)} style={{background:'none', border:'none', color:'#94a3b8', cursor:'pointer', fontSize:12}}>✕</button>
                      </div>
                    </div>
                  ))}
                  <button onClick={()=>setStep('browse')} style={{marginTop:10, fontSize:12, color:'#60a5fa', background:'none', border:'none', cursor:'pointer'}}>+ Add more equipment</button>
                </div>
              )}
              {cart.length === 0 && (
                <div style={{...S.card, padding:14, marginBottom:20, textAlign:'center'}}>
                  <span style={{fontSize:12, color:'#94a3b8'}}>No equipment selected yet — </span>
                  <button onClick={()=>setStep('browse')} style={{fontSize:12, color:'#f97316', background:'none', border:'none', cursor:'pointer', fontWeight:700}}>browse catalog →</button>
                </div>
              )}
              <div style={{...S.card, padding:24}}>
                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12}}>
                  <div>
                    <label style={{fontSize:11, color:'#94a3b8', display:'block', marginBottom:4}}>Your Name *</label>
                    <input style={S.input} placeholder="John Smith" value={quoteForm.name} onChange={e=>setQuoteForm(f=>({...f,name:e.target.value}))} autoFocus />
                  </div>
                  <div>
                    <label style={{fontSize:11, color:'#94a3b8', display:'block', marginBottom:4}}>Company / Business</label>
                    <input style={S.input} placeholder="ABC Contractors LLC" value={quoteForm.company||''} onChange={e=>setQuoteForm(f=>({...f,company:e.target.value}))} />
                  </div>
                  <div>
                    <label style={{fontSize:11, color:'#94a3b8', display:'block', marginBottom:4}}>Email *</label>
                    <input style={S.input} placeholder="john@email.com" type="email" value={quoteForm.email} onChange={e=>setQuoteForm(f=>({...f,email:e.target.value}))} />
                  </div>
                  <div>
                    <label style={{fontSize:11, color:'#94a3b8', display:'block', marginBottom:4}}>Phone</label>
                    <input style={S.input} placeholder="(702) 555-0000" value={quoteForm.phone} onChange={e=>setQuoteForm(f=>({...f,phone:e.target.value}))} />
                  </div>
                  <div>
                    <label style={{fontSize:11, color:'#94a3b8', display:'block', marginBottom:4}}>Fulfillment</label>
                    <select style={S.input} value={quoteForm.fulfillment_type} onChange={e=>setQuoteForm(f=>({...f,fulfillment_type:e.target.value}))}>
                      <option value="pickup">🏪 Customer Pickup</option>
                      <option value="delivery">🚚 Delivery</option>
                    </select>
                  </div>
                  <div>
                    <label style={{fontSize:11, color:'#94a3b8', display:'block', marginBottom:4}}>
                      {quoteForm.fulfillment_type === 'delivery' ? <>Job Site / Delivery Address <span style={{color:'#f97316'}}>*</span></> : 'Job Site Address'}
                    </label>
                    <input style={S.input} placeholder={`1234 Job Site Ave${bizPlace ? `, ${bizPlace}` : ""}`} value={quoteForm.delivery_address} onChange={e=>setQuoteForm(f=>({...f,delivery_address:e.target.value}))} />
                    {quoteForm.fulfillment_type === 'delivery' && <div style={{fontSize:11, color:'#94a3b8', marginTop:3}}>Mileage will be calculated and included in your quote.</div>}
                  </div>
                  <div>
                    <label style={{fontSize:11, color:'#94a3b8', display:'block', marginBottom:4}}>Start Date (approx.)</label>
                    <input style={S.input} type="date" value={quoteForm.from_date} onChange={e=>setQuoteForm(f=>({...f,from_date:e.target.value}))} />
                  </div>
                  <div>
                    <label style={{fontSize:11, color:'#94a3b8', display:'block', marginBottom:4}}>End Date (approx.)</label>
                    <input style={S.input} type="date" value={quoteForm.to_date} onChange={e=>setQuoteForm(f=>({...f,to_date:e.target.value}))} />
                  </div>
                </div>
                <div style={{marginBottom:16}}>
                  <label style={{fontSize:11, color:'#94a3b8', display:'block', marginBottom:4}}>Tell us about your project</label>
                  <textarea style={{...S.input, height:80, resize:'none'}} placeholder="What are you working on? Any special requirements, job site details, quantity needs..." value={quoteForm.message} onChange={e=>setQuoteForm(f=>({...f,message:e.target.value}))} />
                </div>
                <button
                  style={{...S.btn('lg'), width:'100%', opacity:quoteSubmitting?0.6:1, background: (!quoteForm.name||!quoteForm.email)?'#3a3f60':'#f97316'}}
                  onClick={submitQuoteRequest}
                  disabled={quoteSubmitting||!quoteForm.name||!quoteForm.email}>
                  {quoteSubmitting ? '⏳ Sending...' : '📬 Send Quote Request →'}
                </button>
                <div style={{fontSize:11, color:'#94a3b8', textAlign:'center', marginTop:10}}>We'll respond within 1 business day with pricing</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Contract flow */}
      {step === 'contract' && contractOrder && (
        <RentalContractFlow
          order={contractOrder}
          prefill={contractOrder._prefill}
          apiBase={`${API}/api/public/rentals`}
          holdNotice={holdNotice(contractOrder.hold_expires_at)}
          onClose={() => setClosePrompt(true)}
          onComplete={() => { writePending(null); setPending(null); setStep('success'); setContractOrder(null); }}
        />
      )}

      {/* Closing the booking modal: keep or cancel — never a silent strand. */}
      {closePrompt && contractOrder && (
        <div data-testid="close-prompt" style={{position:'fixed', inset:0, zIndex:1100, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', padding:20}}>
          <div style={{background:'#fff', borderRadius:14, padding:24, maxWidth:420, width:'100%', color:'#0f172a'}}>
            <div style={{fontSize:16, fontWeight:800, marginBottom:6}}>Leave this booking?</div>
            <div style={{fontSize:13, color:'#374151', lineHeight:1.5, marginBottom:16}}>
              Order <strong>{contractOrder.order_number}</strong> is holding your equipment until <strong>{holdUntilText(contractOrder.hold_expires_at)}</strong>. Keep it and you can come back to sign and pay before then; cancel it and the equipment is released right away.
            </div>
            <div style={{display:'flex', gap:10}}>
              <button disabled={pendingBusy} onClick={() => { setClosePrompt(false); setStep('cart'); setContractOrder(null); }} style={{flex:1, padding:'11px 0', borderRadius:8, border:'1px solid #e5e7eb', background:'#fff', color:'#0f172a', fontWeight:700, cursor:'pointer'}}>Keep this booking for later</button>
              <button disabled={pendingBusy} onClick={() => cancelPending(contractOrder.public_token)} style={{flex:1, padding:'11px 0', borderRadius:8, border:'none', background:'#7f1d1d', color:'#fff', fontWeight:700, cursor:'pointer'}}>Cancel booking</button>
            </div>
            <button onClick={() => setClosePrompt(false)} style={{marginTop:10, background:'none', border:'none', color:'#64748b', fontSize:12, cursor:'pointer', width:'100%'}}>Back to signing</button>
          </div>
        </div>
      )}

      <div style={{borderTop:'1px solid #2e3150', padding:'24px 20px', textAlign:'center', marginTop:40}}>
        <div style={{fontSize:12, color:'#94a3b8'}}>{business.name}{bizPlace ? ` • ${bizPlace}` : ''}</div>
        <div style={{fontSize:11, color:'#3a3f60', marginTop:4}}>Powered by <a href="https://ivisionmarketing.com" style={{color:'#f97316', textDecoration:'none'}}>IVision CRM</a></div>
      </div>
    </div>
  );
}
